import { useState } from 'react';
import { CompoundKnowledgeGraph } from '@/components/CompoundKnowledgeGraph';
import { NetworkAnalyticsPanel } from '@/components/NetworkAnalyticsPanel';
import { PathFinder } from '@/components/PathFinder';
import { Button } from '@/components/ui/button';
import { BarChart3, Route, Box } from 'lucide-react';
import { useLocation } from 'wouter';
import { useDatabase } from '@/hooks/useDatabase';

export default function KnowledgeGraph() {
  const [, navigate] = useLocation();
  const { database, loading, error } = useDatabase();
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showPathFinder, setShowPathFinder] = useState(false);
  const [highlightedPath, setHighlightedPath] = useState<string[]>([]);

  const handleNodeClick = (node: any) => {
    // Navigate to relevant page based on node type
    if (node.type === 'study' && node.data?.study_id) {
      navigate(`/study/${node.data.study_id}`);
    }
  };

  if (loading || !database) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-[#0D0D0D] via-[#161513] to-[#0D0D0D]">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-[#C8A53D] border-t-transparent mx-auto" />
          <p className="text-lg text-[#EDEAE0]">Loading knowledge graph...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-[#0D0D0D] via-[#161513] to-[#0D0D0D]">
        <div className="text-center max-w-2xl mx-auto p-8">
          <p className="text-lg text-red-400 mb-4">Error loading database: {error}</p>
          <Button onClick={() => window.location.reload()} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Validate database has required data
  const hasRequiredData = database.mushrooms?.length > 0 &&
                          database.compounds?.length > 0 &&
                          database.conditions?.length > 0 &&
                          database.studies?.length > 0;

  if (!hasRequiredData) {
    console.error('[KnowledgeGraph] Missing required data:', {
      mushrooms: database.mushrooms?.length ?? 0,
      compounds: database.compounds?.length ?? 0,
      conditions: database.conditions?.length ?? 0,
      studies: database.studies?.length ?? 0
    });
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-[#0D0D0D] via-[#161513] to-[#0D0D0D]">
        <div className="text-center max-w-2xl mx-auto p-8">
          <p className="text-lg text-[#C8A53D] mb-4">
            Insufficient data to render graph. Please ensure all data files are loaded correctly.
          </p>
          <Button onClick={() => window.location.reload()} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0D0D0D] via-[#161513] to-[#0D0D0D] p-4 md:p-8">
      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-4xl font-bold text-white md:text-5xl">
            FungAlchemy Knowledge Graph
          </h1>
          <p className="text-lg text-[#EDEAE0]">
            Explore the interconnected web of mushroom research
          </p>
          <p className="mt-2 text-sm text-[#C8A53D]">
            {database.studies.length} studies • {database.compounds.length} compounds •
            {database.mushrooms.length} mushroom species • {database.conditions.length} conditions
          </p>

          {/* Toggle Buttons */}
          <div className="mt-4 flex gap-3 justify-center flex-wrap">
            <Button
              onClick={() => navigate('/knowledge-graph-3d')}
              variant="outline"
              className="gap-2 bg-gradient-to-r from-[#A98A2C] to-[#8A6F22] hover:from-[#8A6F22] hover:to-[#8A6F22] text-white border-0"
            >
              <Box className="h-4 w-4" />
              Switch to 3D View
            </Button>
            <Button
              onClick={() => setShowAnalytics(!showAnalytics)}
              variant={showAnalytics ? "default" : "outline"}
              className="gap-2"
            >
              <BarChart3 className="h-4 w-4" />
              {showAnalytics ? 'Hide Analytics' : 'Show Network Analytics'}
            </Button>
            <Button
              onClick={() => setShowPathFinder(!showPathFinder)}
              variant={showPathFinder ? "default" : "outline"}
              className="gap-2"
            >
              <Route className="h-4 w-4" />
              {showPathFinder ? 'Hide Path Finder' : 'Find Connection'}
            </Button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-8">
            <NetworkAnalyticsPanel database={database} isVisible={showAnalytics} />
          </div>
        )}

        {/* Path Finder Panel */}
        {showPathFinder && (
          <div className="mb-8">
            <PathFinder
              database={database}
              onPathFound={(path) => setHighlightedPath(path)}
              onClose={() => {
                setShowPathFinder(false);
                setHighlightedPath([]);
              }}
            />
          </div>
        )}

        {/* Graph Component */}
        <CompoundKnowledgeGraph
          data={database}
          height={700}
          onNodeClick={handleNodeClick}
          highlightedPath={highlightedPath}
        />

        {/* Legend and Instructions */}
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-[#33312B] bg-[#161513]/50 p-6 backdrop-blur">
            <h3 className="mb-4 text-xl font-semibold text-white">How to Use</h3>
            <ul className="space-y-2 text-sm text-[#EDEAE0]">
              <li>• <strong>Click and drag</strong> to pan around the graph</li>
              <li>• <strong>Scroll</strong> to zoom in and out</li>
              <li>• <strong>Click a node</strong> to see details (studies are clickable to view full details)</li>
              <li>• <strong>Use filters</strong> to show/hide different node types</li>
              <li>• <strong>Search</strong> to find specific compounds, studies, or conditions</li>
              <li>• <strong>Download</strong> the graph as an image for presentations</li>
            </ul>
          </div>

          <div className="rounded-lg border border-[#33312B] bg-[#161513]/50 p-6 backdrop-blur">
            <h3 className="mb-4 text-xl font-semibold text-white">Graph Legend</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="h-4 w-4 rounded-full bg-[#A98A2C]" />
                <span className="text-sm text-[#EDEAE0]">
                  <strong>Studies</strong> - Clinical trials and research papers
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="h-4 w-4 rounded-full bg-[#C26A1E]" />
                <span className="text-sm text-[#EDEAE0]">
                  <strong>Compounds</strong> - Bioactive compounds found in mushrooms
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="h-4 w-4 rounded-full bg-[#C8A53D]" />
                <span className="text-sm text-[#EDEAE0]">
                  <strong>Mushrooms</strong> - Different mushroom species
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="h-4 w-4 rounded-full bg-[#C8A53D]" />
                <span className="text-sm text-[#EDEAE0]">
                  <strong>Conditions</strong> - Health conditions studied
                </span>
              </div>
              <div className="mt-4 border-t border-[#33312B] pt-3">
                <p className="text-xs text-[#C8A53D]">
                  <strong>Lines</strong> represent relationships: studies contain compounds,
                  test mushroom species, and target specific conditions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
